import React, { FC, useRef, useEffect, useMemo, useCallback } from 'react';
import CN from 'classnames';
import {
    Scene,
    WebGLRenderer,
    PerspectiveCamera,
    AmbientLight,
    DirectionalLight,
} from 'three';
import { useAnimationFrame } from '@app/react/3d/useAnimationFrame';
import { fetchKintekiModel } from '@app/react/common/loadModel';
import { useMediaDesktop, useMediaMobile } from '@app/react/common/useMedia';
import { ResizeEventHandler } from '@app/react/uikit/ResizeEventHandler';
import { ArrowModel } from '@app/react/3d/ArrowModelInterface';
import { toRad } from '@app/react/common/toRad';
import { createRenderer, createLight } from '@app/react/3d/utils/order3d';

const desktop = {
    pos: { x: -8.5, y: -12, z: 0 },
    rot: { x: 0, y: 0, z: toRad(54) },
} as const;

const mobile = {
    pos: { x: -9.8, y: -11.5, z: 0 },
    rot: { x: 0, y: 0, z: toRad(52) },
} as const;

export interface Background3dProps {
    className?: string;
}

export const Background3d: FC<Background3dProps> = (props) => {
    const { className = null } = props;

    const isDesktop = useMediaDesktop();
    const isMobile = useMediaMobile();

    const container = useRef<HTMLCanvasElement>(null);
    const rendererRef = useRef<WebGLRenderer | null>(null);
    const cameraRef = useRef<PerspectiveCamera | null>(null);
    const modelRef = useRef<ArrowModel | null>(null);
    const { width, height } = ResizeEventHandler();

    const rootClassName = CN('background-3d', className);

    const scene = useMemo(() => {
        const s = new Scene();
        return s;
    }, []);

    useMemo(() => {
        const {
            hemisphereLight: h,
            ambientLight: ambLight,
            directionalLights: dirLights,
        } = createLight();

        scene.add(h);
        scene.add(ambLight);
        dirLights.forEach((l) => scene.add(l));
    }, [scene]);

    useEffect(() => {
        // 93 is 適当なデフォルト値
        const clientWidth = container.current?.clientWidth || 93;
        const clientHeight = container.current?.clientHeight || 93;
        console.log('[background3d] renderer size:', clientWidth, clientHeight);

        if (container.current === null) {
            console.error('canvas refer null');
            return;
        }

        rendererRef.current = createRenderer(
            container.current,
            clientWidth,
            clientHeight,
            window.devicePixelRatio,
        );
        rendererRef.current.setClearColor(0xff0000, 0);

        const camera = new PerspectiveCamera(
            30,
            clientWidth / clientHeight,
            0.01,
            2000,
        );
        camera.position.set(0, 0, 20);

        camera.updateMatrixWorld();
        cameraRef.current = camera;
    }, [scene]);

    useEffect(() => {
        const clientWidth = container.current?.clientWidth || 500;
        const clientHeight = container.current?.clientHeight || 500;
        console.log('[3d] resize renderer size:', clientWidth, clientHeight);

        rendererRef.current?.setSize(clientWidth, clientHeight, false);
        if (cameraRef.current) {
            cameraRef.current.aspect = clientWidth / clientHeight;
        }
        cameraRef.current?.updateProjectionMatrix();
    }, [container, rendererRef, width, height]);

    useEffect(() => {
        const f = async (): void => {
            const model = await fetchKintekiModel(false);
            scene.add(model.model);
            modelRef.current = model;

            model.model.position.set(-9.8, -11.5, 0);
            model.model.rotation.set(0, 0, toRad(52));
        };

        f();
    }, [scene, rendererRef, cameraRef, modelRef]);

    useEffect(() => {
        const pos = isMobile ? mobile.pos : desktop.pos;
        const rot = isMobile ? mobile.rot : desktop.rot;
        modelRef.current?.model.position.set(pos.x, pos.y, pos.z);
        modelRef.current?.model.rotation.set(rot.x, rot.y, rot.z);
    }, [isDesktop, isMobile, modelRef]);

    const loop = useCallback(() => {
        if (cameraRef.current) {
            rendererRef.current?.render(scene, cameraRef.current);
        }
    }, [rendererRef, scene, cameraRef]);
    useAnimationFrame(loop, 30);

    return <canvas className={rootClassName} ref={container} />;
};

Background3d.displayName = 'Background3d';
